import type { Theme } from '@emotion/react';
import { Box, svgIconClasses } from '@mui/material';
import type { SxProps } from '@mui/material';
import React from 'react';

import { ChevronDown } from '@tbd/life-jsx/SystemIcons';

export { Accordion, AccordionDetails, AccordionItem, AccordionSummary };

const DEFAULT_SX = {};
const SUMMARY_EXPAND_ICON_CLASS_NAME = 'summary-expand-icon';

function Accordion(props: { children: React.ReactNode; sx?: SxProps<Theme> }) {
  const { children, sx = DEFAULT_SX } = props;
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '32rem',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function AccordionItem(props: {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  summaryProps?: {
    sx?: SxProps<Theme>;
  };
  open?: boolean;
}) {
  const { children, sx = DEFAULT_SX } = props;
  const childElements = React.Children.toArray(children).filter(
    React.isValidElement
  );
  const summaryContent = childElements.find(
    (child) => child.type === AccordionSummary
  );
  const detailsContent = childElements.find(
    (child) => child.type === AccordionDetails
  );

  return (
    <Box
      component="details"
      open={props.open}
      sx={{
        '& summary': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          '&:focus': {
            outline: 'none',
            bgcolor: 'action.hover',
          },
          ...(props.summaryProps?.sx ?? {}),
        },
        [`& summary .${SUMMARY_EXPAND_ICON_CLASS_NAME} .${svgIconClasses.root}`]:
          {
            transition: 'transform 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        [`&[open] > summary .${SUMMARY_EXPAND_ICON_CLASS_NAME} .${svgIconClasses.root}`]:
          {
            transform: 'rotate(180deg)',
          },
        ...sx,
      }}
    >
      {summaryContent}
      {detailsContent}
    </Box>
  );
}

function AccordionDetails(props: {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}) {
  const { children, sx = DEFAULT_SX } = props;
  return <Box sx={sx}>{children}</Box>;
}

function AccordionSummary(props: {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
  expandIcon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
}) {
  const { children, expandIcon, sx = DEFAULT_SX, iconPosition = 'end' } = props;
  return (
    <Box
      component="summary"
      sx={{
        marginBlockEnd: 2,
        ...sx,
      }}
    >
      {iconPosition === 'start' ? (
        <SummaryExpandIcon>{expandIcon}</SummaryExpandIcon>
      ) : null}
      {children}
      {iconPosition === 'end' ? (
        <SummaryExpandIcon>{expandIcon}</SummaryExpandIcon>
      ) : null}
    </Box>
  );
}

function SummaryExpandIcon(props: { children?: React.ReactNode }) {
  const { children } = props;

  return (
    <div className={SUMMARY_EXPAND_ICON_CLASS_NAME}>
      {children ?? <ChevronDown />}
    </div>
  );
}
